import { Payload, getShippingCost } from 'api/shipping-cost'
import { Currencies } from 'constants/currencies'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import { useMemo } from 'react'
import { UseQueryOptions, useQuery } from 'react-query'

type Options = Omit<
  UseQueryOptions<number | undefined, unknown, number | undefined, any[]>,
  'queryKey' | 'queryFn' | 'refetchOnMount' | 'refetchOnWindowFocus'
> & { convertToUserCurrency?: boolean }

/**
 * Determines the shipping cost for a given payload.
 */
export const useShippingCost = (payload: Payload, options?: Options) => {
  const { getConvertedValue } = useCurrencies()

  const { data, ...rest } = useQuery(
    [
      'shippingFee',
      payload.countryCode,
      payload.buyingPrice,
      payload.medium,
      payload.shipmentCategory,
      payload.size,
      payload.framed,
      payload.galleryName,
      payload.shipmentWeightOverride,
    ],
    async () => {
      const res = await getShippingCost(payload)

      if (!res) return 0

      if (typeof res === 'number') {
        return res / 100
      }

      if (res.error) {
        console.error(res.error.message)
        return 0
      }

      return 0
    },
    { ...options, refetchOnMount: true, refetchOnWindowFocus: false }
  )

  // if needed, convert to user currency and round the result
  const _data = useMemo(() => {
    let value = data

    if (options?.convertToUserCurrency) {
      value = getConvertedValue(data, {
        from: Currencies.GBP,
        to: Currencies.USER,
      })
    }

    if (!value) return 0

    return Math.ceil(value)
  }, [data, options?.convertToUserCurrency, getConvertedValue])

  return { data: _data, ...rest }
}
